"use client";

import type { TrackedAccountSummary } from "./types";
import ListAvatar from "@/components/peeqd/tracking/ListAvatar";

interface AccountListProps {
  accounts: TrackedAccountSummary[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

function AccountAvatar({
  avatarUrl,
  active,
}: {
  avatarUrl?: string | null;
  active: boolean;
}) {
  const ring = active ? "ring-2 ring-rose/30" : "";
  return (
    <ListAvatar
      avatarUrl={avatarUrl}
      size="sm"
      className={`h-10 w-10 ${ring}`}
    />
  );
}

export default function AccountList({
  accounts,
  selectedId,
  onSelect,
}: AccountListProps) {
  if (accounts.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-line bg-surface/60 p-6 text-center text-sm text-muted">
        No tracked accounts yet.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {accounts.map((account) => {
        const active = account.id === selectedId;

        return (
          <button
            key={account.id}
            type="button"
            onClick={() => onSelect(account.id)}
            className={`w-full rounded-2xl border px-3 py-2.5 text-left transition sm:px-4 sm:py-3 ${
              active
                ? "border-rose/40 bg-rose/5 shadow-soft"
                : "border-line bg-surface hover:border-rose/20"
            }`}
          >
            <div className="flex items-center gap-3">
              <AccountAvatar avatarUrl={account.avatarUrl} active={active} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate font-semibold text-ink">@{account.instagramUsername}</span>
                  <span className="shrink-0 rounded-full border border-line px-2 py-0.5 text-[10px] font-medium uppercase text-muted">
                    {account.planTier}
                  </span>
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted">
                  {account.scrape.isScraping ? (
                    <span className="text-rose">Analyzing...</span>
                  ) : account.scrape.lastSuccessAt ? (
                    <span>Live</span>
                  ) : (
                    <span>Pending scrape</span>
                  )}
                  {account.scrape.pendingJobs > 0 && (
                    <span>{account.scrape.pendingJobs} queued</span>
                  )}
                </div>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}
